// ═══════════════════════════════════════════════════════════════════════════
// FILE: src/app/components/eisenhower-panel/eisenhower-panel.component.ts
// DATE: 2026-10-23
// CREATOR: PIA (Rolle HUGO), beauftragt vom PO am 23.10.2026
// ceeq.it: _40 Eisenhower-Fenster (Alt+N) · baut auf _40 #245 (Signale) und _35 #181
// Handbuch: docs/HANDBUCH_eisenhower-fenster.md
//
// PURPOSE
//   „Wo muss ich jetzt hin?“ — auf jeder Seite mit Alt+N. Vier Felder je Nutzer:
//     Do now     nur du kannst es, und es brennt
//     Plan       nur du kannst es, wichtig — in dieser Reihenfolge
//     Delegate   jemand anderes kann es — an wen; „Delegate“ öffnet Alt+P mit Entwurf
//     Drop       weder wichtig noch dringend, still oder geparkt
//   Unten: die Selbstbewertung (ist „Do now“ seit dem letzten Blick vorangekommen?)
//   und aufklappbar die Rechenregeln. Daten: GET /api/me/eisenhower.
//   Knopf unten links über „Ask“; sein Zähler = Einträge in „Do now“ (alle 10 min neu).
//
// TESTIDS: eis.* (D4) — Liste im Template.
// ═══════════════════════════════════════════════════════════════════════════

import { Component, ElementRef, HostListener, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { RequestsService, anchorLink } from '../../services/requests.service';

export type Quadrant = 'do_now' | 'plan' | 'delegate' | 'drop';

export interface EisDelegate { kind: 'role' | 'person'; role?: string; label?: string; uuid?: string; why: string; }
export interface EisEntry {
  verb: string;
  mine: boolean;
  delegate: EisDelegate | null;
  target: { pslot: string; typ: string; id: number; name: string | null };
  reason: string;
  signal_ids: string[];
  importance: { score: number; parts: Record<string, number>; value_source: string; parked: boolean };
  urgency: { score: number; parts: Record<string, number> };
  money_path: boolean;
  rank: number;
  waiting_people: number;
  quadrant: Quadrant;
  more: Array<{ verb: string; delegate: EisDelegate | null; reason: string }>;
}
export interface EisSelfRun {
  ts: string; since_min: number; version: string;
  do_now: { n: number; moved: number; done: number; idle: number; hit_rate: number | null };
  plan: { n: number; moved: number; done: number; idle: number; hit_rate: number | null };
}
export interface EisResponse {
  status: string; version: string; generated_at: string; read_ms: number; count: number; truncated: boolean;
  quadrants: Record<Quadrant, EisEntry[]>;
  counts: Record<Quadrant, number>;
  money_path: number;
  people: Record<string, { uuid: string; display_name?: string; name?: string; is_agent?: boolean }>;
  self_check: { runs: EisSelfRun[]; do_now_hit_rate_avg: number | null; note: string; evaluated_now: boolean; next_evaluation_after_min: number };
  explain: { directive: string; money_path: string; importance: string; urgency: string; selection: string;
             quadrants: Record<Quadrant, string> };
}

const POLL_MS = 10 * 60_000;

@Component({
  selector: 'app-eisenhower-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './eisenhower-panel.component.html',
  styleUrls: ['./eisenhower-panel.component.scss'],
})
export class EisenhowerPanelComponent {
  private http = inject(HttpClient);
  private router = inject(Router);
  readonly req = inject(RequestsService);

  @ViewChild('closeBtn') closeBtn?: ElementRef<HTMLButtonElement>;

  readonly quadrants: Array<{ key: Quadrant; title: string; sub: string }> = [
    { key: 'do_now', title: 'Do now', sub: 'Only you can do it — and it is burning' },
    { key: 'plan', title: 'Plan', sub: 'Only you can do it — important, in this order' },
    { key: 'delegate', title: 'Delegate', sub: 'Someone else can do it — to whom' },
    { key: 'drop', title: 'Drop or park', sub: 'Neither important nor urgent' },
  ];

  open = signal(false);
  loading = signal(false);
  error = signal<string | null>(null);
  data = signal<EisResponse | null>(null);
  showExplain = signal(false);
  expanded = signal<string | null>(null);

  readonly doNowCount = computed(() => this.data()?.counts.do_now ?? 0);
  readonly lastRun = computed(() => this.data()?.self_check.runs[0] ?? null);

  private readonly api = (() => {
    const host = window.location.hostname;
    return host.includes('localhost') || host === '127.0.0.1' ? 'http://localhost:3000' : `https://api.${host}`;
  })();

  constructor() {
    this.load(true);
    setInterval(() => { if (!this.open()) this.load(true); }, POLL_MS);
  }

  // ── Tastatur ───────────────────────────────────────────────────────────
  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (e.altKey && !e.ctrlKey && !e.metaKey && (e.key === 'n' || e.key === 'N' || e.code === 'KeyN')) {
      e.preventDefault();
      this.toggle();
      return;
    }
    if (e.key === 'Escape' && this.open()) { e.stopPropagation(); this.close(); }
  }

  toggle(): void { if (this.open()) this.close(); else this.show(); }

  show(): void {
    this.open.set(true);
    this.load(false);
    setTimeout(() => this.closeBtn?.nativeElement.focus(), 0);
  }

  close(): void { this.open.set(false); this.expanded.set(null); }

  load(still: boolean): void {
    if (!still) { this.loading.set(true); this.error.set(null); }
    this.http.get<EisResponse>(`${this.api}/api/me/eisenhower`, { withCredentials: true }).subscribe({
      next: (r) => {
        this.data.set(r); this.loading.set(false);
        console.log('[Eisenhower] geladen do_now=%s plan=%s delegate=%s drop=%s ms=%s', r.counts.do_now, r.counts.plan, r.counts.delegate, r.counts.drop, r.read_ms);
      },
      error: (e) => {
        this.loading.set(false);
        if (!still) this.error.set(e?.status === 503 ? 'The overview is temporarily unavailable.' : 'Could not load your priorities.');
        console.warn('[Eisenhower] Laden fehlgeschlagen status=%s', e?.status);
      },
    });
  }

  // ── Anzeige-Helfer ─────────────────────────────────────────────────────
  key(e: EisEntry): string { return `${e.quadrant}-${e.target.typ}-${e.target.id}`; }
  toggleMore(e: EisEntry): void { this.expanded.set(this.expanded() === this.key(e) ? null : this.key(e)); }
  pslotShort(p: string): string { const m = p.match(/_(\d+)$/); return m ? `_${m[1]}` : p; }

  delegateName(d: EisDelegate | null): string {
    if (!d) return '';
    if (d.kind === 'role') return d.label || d.role || '';
    const p = d.uuid ? this.data()?.people[d.uuid] : null;
    return p?.display_name || p?.name || 'the owner';
  }

  urgencyWhy(e: EisEntry): string {
    return Object.entries(e.urgency.parts).filter(([, v]) => v > 0).map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`).join(' · ') || 'none';
  }

  ageMin(iso: string): string {
    const min = Math.round((Date.now() - Date.parse(iso)) / 60_000);
    return min < 60 ? `${min} min ago` : min < 1440 ? `${Math.round(min / 60)} h ago` : `${Math.round(min / 1440)} d ago`;
  }

  // ── Handeln ────────────────────────────────────────────────────────────
  go(e: EisEntry): void {
    const link = anchorLink(e.target.pslot, e.target.typ, e.target.id);
    if (link) { this.close(); this.router.navigateByUrl(link); }
  }

  delegate(e: EisEntry, d: EisDelegate | null = e.delegate, verb = e.verb, reason = e.reason): void {
    if (!d) return;
    this.close();
    this.req.openWithDraft({
      pslot: e.target.pslot, anchorType: e.target.typ, anchorId: e.target.id,
      to: { role: d.role, uuid: d.uuid, label: d.label },
      appeal: `please: ${verb} — ${e.target.typ} #${e.target.id} "${e.target.name ?? ''}"`,
      statusStmnt: reason,
      imessage: `My priority list (Alt+N) puts this under Delegate: ${d.why}.`,
    });
  }
}
